"use strict";
/**
 * @fileoverview Math module contains utility methods.
 *
 * All exported functions from this module should be pure functions.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.cube = exports.isCube = exports.cubeFaces = exports.perspective = exports.lookAt = exports.rotate = exports.scale = exports.translate = exports.multiply = exports.identity = exports.add = exports.isVector3 = exports.isPowerOfTwo = void 0;
/**
 * Returns if a number is a power of two.
 */
exports.isPowerOfTwo = (n) => ((n & (n - 1)) === 0);
/**
 * Checks if an object is a Vector3.
 */
exports.isVector3 = (obj) => Array.isArray(obj) && obj.length === 3 && obj.every(x => !isNaN(x));
const normalize = (v) => {
    const len = Math.hypot(...v);
    if (!len) {
        throw new Error('Cannot normalize a vector with no length');
    }
    return v.map(x => x / len);
};
/**
 * Add two vectors.
 */
exports.add = (a, b) => a.map((cur, i) => cur + b[i]);
const subtract = (a, b) => a.map((cur, i) => cur - b[i]);
const dot = (a, b) => a.reduce((acc, cur, i) => acc + (cur * b[i]), 0);
const cross = (a, b) => [
    (a[1] * b[2]) - (a[2] * b[1]),
    (a[2] * b[0]) - (a[0] * b[2]),
    (a[0] * b[1]) - (a[1] * b[0]),
];
const matDimension = (arr) => Math.sqrt(arr.length);
const vecDimension = (arr) => arr.length;
const zeros = (dim) => [...Array(Math.pow(dim, 2))].map(() => 0);
const zeroVector = (dim) => [...Array(dim)].map(() => 0);
/**
 * Initialize an identity matrix with the provided dimension.
 */
exports.identity = (dim) => {
    const M = zeros(dim);
    for (let i = 0; i < dim; i++) {
        M[(i * dim) + i] = 1;
    }
    return M;
};
const multiplyM = (A, B, d) => {
    const result = zeros(d);
    for (let i = 0; i < d; i++)
        for (let j = 0; j < d; j++)
            for (let k = 0; k < d; k++) {
                result[(d * i) + j] += A[(d * i) + k] * B[(d * k) + j];
            }
    return result;
};
const multiplyMv = (A, b, d) => {
    const result = zeroVector(d);
    for (let i = 0; i < d; i++)
        for (let j = 0; j < d; j++) {
            result[i] += A[(i * d) + j] * b[j];
        }
    return result;
};
/**
 * Can be used for matrix-matrix and matrix-vector multiplication.
 */
exports.multiply = (A, B) => {
    const d = matDimension(A);
    if (d !== matDimension(B)) {
        if (d !== vecDimension(B)) {
            throw new Error('Cannot multiply, incompatible dimensions');
        }
        return multiplyMv(A, B, d);
    }
    return multiplyM(A, B, d);
};
/**
 * Apply a 3D transmation to a 4-dimensional matrix, M.
 */
exports.translate = (M, x, y, z) => {
    const result = M.slice(0, 16);
    result[12] += x;
    result[13] += y;
    result[14] += z;
    return result;
};
/**
 * Apply a scale transformation to a matrix.
 */
exports.scale = (A, ...args) => {
    if (!args.length) {
        throw new Error('You must provide at least one number to scale a matrix');
    }
    const d = matDimension(A);
    if (d === 4 && args.length === 2) {
        throw new Error('You must provide 1, 3, or 4 arguments to scale for a 4D matrix');
    }
    const S = exports.identity(d);
    for (let i = 0; i < d; i++) {
        if (i === 3 && exports.scale.length < 4) {
            S[(d * i) + i] = 1;
        }
        else {
            S[(d * i) + i] = Number(isNaN(args[i]) ? args[0] : args[i]);
        }
    }
    return exports.multiply(S, A);
};
const quatToRotationMat = (q) => [
    // first column
    1 - (2 * ((Math.pow(q[2], 2)) + (Math.pow(q[3], 2)))),
    2 * ((q[1] * q[2]) + (q[3] * q[0])),
    2 * ((q[1] * q[3]) - (q[2] * q[0])),
    // second column
    2 * ((q[1] * q[2]) - (q[3] * q[0])),
    1 - (2 * ((Math.pow(q[1], 2)) + (Math.pow(q[3], 2)))),
    2 * ((q[2] * q[3]) + (q[1] * q[0])),
    // third column
    2 * ((q[1] * q[3]) + (q[2] * q[0])),
    2 * ((q[2] * q[3]) - (q[1] * q[0])),
    1 - (2 * ((Math.pow(q[1], 2)) + (Math.pow(q[2], 2)))),
];
const rotate2 = (M, theta) => {
    const c = Math.cos(theta);
    const s = Math.sin(theta);
    return exports.multiply([c, s, c, -s], M);
};
/**
 * Apply a 3D rotation of theta radians around the given axis
 * to a 4D matrix.
 */
exports.rotate = (A, theta, ...axis) => {
    if (isNaN(theta)) {
        throw new Error('Expected a number as a 2nd argument');
    }
    const d = matDimension(A);
    if (d === 2)
        return rotate2(A, theta);
    axis = axis.slice(0, 3);
    if (!exports.isVector3(axis)) {
        throw new Error('Expected numeric 3rd, 4th, and 5th argument');
    }
    axis = normalize(axis);
    const s = Math.sin(theta / 2);
    const q = [
        Math.cos(theta / 2),
        axis[0] * s,
        axis[1] * s,
        axis[2] * s,
    ];
    const R = quatToRotationMat(q);
    if (d === 3)
        return exports.multiply(A, R);
    const R4 = [
        R[0], R[1], R[2], 0,
        R[3], R[4], R[5], 0,
        R[6], R[7], R[8], 0,
        0, 0, 0, 1,
    ];
    return exports.multiply(A, R4);
};
/**
 * Compute the 4D view matrix for a camera at
 * position "eye", looking at position "at", oriented
 * with "up" facing in the y+ direction.
 *
 * Based on https://github/toji/gl-matrix
 */
exports.lookAt = (eye, at, up, epsilon = 1e-6) => {
    let z = subtract(eye, at);
    let count = 0;
    for (let i = 0; i < 3; i++) {
        if (Math.abs(z[i]) < epsilon)
            count++;
    }
    if (count === 3)
        return exports.identity(4);
    z = normalize(z);
    let x;
    try {
        x = normalize(cross(up, z));
    }
    catch (ok) {
        x = [0, 0, 0];
    }
    let y;
    try {
        y = normalize(cross(z, x));
    }
    catch (ok) {
        y = [0, 0, 0];
    }
    return [
        x[0], y[0], z[0], 0,
        x[1], y[1], z[1], 0,
        x[2], y[2], z[2], 0,
        -dot(x, eye), -dot(y, eye), -dot(z, eye), 1,
    ];
};
/**
 * Generate a 4D perspective matrix from
 * @param {fovy} vertical field of view (radians)
 * @param {aspect} width / height aspect ratio
 * @param {near} near bound of the frustum
 * @param {far} far bound of the frustum, can be Infinity or null
 */
exports.perspective = (fovy, aspect, near, far) => {
    const f = 1.0 / Math.tan(fovy / 2);
    let out10;
    let out14;
    if (far !== null && far !== Infinity) {
        const nf = 1 / (near - far);
        out10 = (near + far) * nf;
        out14 = 2 * far * near * nf;
    }
    else {
        out10 = -1;
        out14 = -2 * near;
    }
    return [
        f / aspect, 0, 0, 0,
        0, f, 0, 0,
        0, 0, out10, -1,
        0, 0, out14, 0,
    ];
};
/**
 * Returns an array of all the cube face keys.
 */
exports.cubeFaces = () => ['posx', 'negx', 'posy', 'negy', 'posz', 'negz'];
/**
 * Test if an object is a Cube.
 */
exports.isCube = (obj) => Boolean(obj) && exports.cubeFaces().every(k => obj[k] !== undefined);
/**
 * Build a cube by iterating over each face and calling
 * a given callback.
 */
exports.cube = (buildFace) => {
    const result = {};
    for (const cf of exports.cubeFaces()) {
        result[cf] = buildFace(cf);
    }
    return result;
};
